import React, { useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '../../../../../../shared/components';
import {
  EnergyConsumption,
  EnergyEfficiency,
  RoadMaintenance,
  Streetlights,
} from '../../../../../../services/types/Calculator';
import StreetlightSection from '../Modules/StreetlightSection';
import RoadMaintenanceSection from '../Modules/RoadMaintenanceSection';
import EnergyEfficiencySection from '../Modules/EnergyEfficiencySection';
import EnergyConsumptionSection from '../Modules/EnergyConsumptionSection';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

export interface TeamMember {
  imgPath?: string;
  name: string;
  initials: string;
}
interface Props {
  streetlights: Streetlights;
  setStreetlights: React.Dispatch<React.SetStateAction<Streetlights>>;
  roadMaintenance: RoadMaintenance;
  setRoadMaintenance: React.Dispatch<React.SetStateAction<RoadMaintenance>>;
  energyEfficiency: EnergyEfficiency;
  setEnergyEfficiency: React.Dispatch<React.SetStateAction<EnergyEfficiency>>;
  energyConsumptionOperation: EnergyConsumption;
  setEnergyConsumptionOperation: React.Dispatch<React.SetStateAction<EnergyConsumption>>;
}

const OperationTab: React.FC<Props> = ({
  energyConsumptionOperation,
  setEnergyConsumptionOperation,
  streetlights,
  setStreetlights,
  roadMaintenance,
  setRoadMaintenance,
  energyEfficiency,
  setEnergyEfficiency,
}) => {
  const { t } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleRowToggle = (index: number) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter((rowIndex) => rowIndex !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const chevron = (index: number) =>
    expandedRows.includes(index) ? 'chevron-down' : 'chevron-right';

  return (
    <div className="timeline-list">
      {energyConsumptionOperation && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(0)}
                text={energyConsumptionOperation.name}
                type="button"
                onClick={() => handleRowToggle(0)}
              />
              {expandedRows.includes(0) && (
                <>
                  <span id={`tr-energyConsumptionOperation-${energyConsumptionOperation.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-energyConsumptionOperation-${energyConsumptionOperation.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.EnergyConsumption')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(0) && energyConsumptionOperation.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal:{' '}
                <NumberFormatterSinSeparador value={energyConsumptionOperation.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(0) && (
            <EnergyConsumptionSection
              energyConsumption={energyConsumptionOperation}
              onUpdateEnergyConsumption={(updatedEnergyConsumptionOperation) =>
                setEnergyConsumptionOperation(updatedEnergyConsumptionOperation)
              }
            />
          )}
        </div>
      )}

      {streetlights && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1"
                icon={chevron(1)}
                text={streetlights.name}
                type="button"
                onClick={() => handleRowToggle(1)}
              />
            </div>
            {!expandedRows.includes(1) && streetlights.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={streetlights.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(1) && (
            <StreetlightSection
              streetlights={streetlights}
              onUpdateStreetlights={(updatedStreetlights) => setStreetlights(updatedStreetlights)}
            />
          )}
        </div>
      )}

      {roadMaintenance && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(2)}
                text={roadMaintenance.name}
                type="button"
                onClick={() => handleRowToggle(2)}
              />
            </div>
            {!expandedRows.includes(2) && roadMaintenance.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={roadMaintenance.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(2) && (
            <RoadMaintenanceSection
              roadMaintenance={roadMaintenance}
              onUpdateRoadMaintenance={(updatedRoadMaintenance) =>
                setRoadMaintenance(updatedRoadMaintenance)
              }
            />
          )}
        </div>
      )}

      {energyEfficiency && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(3)}
                text={energyEfficiency.name}
                type="button"
                onClick={() => handleRowToggle(3)}
              />
            </div>
            {!expandedRows.includes(3) && energyEfficiency.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={energyEfficiency.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(3) && (
            <EnergyEfficiencySection
              energyEfficiency={energyEfficiency}
              onUpdateEnergyEfficiency={(updatedEnergyEfficiency) =>
                setEnergyEfficiency(updatedEnergyEfficiency)
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default OperationTab;
